import { useMemo } from "react";
import type { Edge, Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { NODE_LABELS } from "../../lib/operationPorts.js";
import { loopPairName, resolveLoopPairing } from "../../lib/loopTopology.js";
import { NodeOutputStats, type NodeOutputRecord } from "./nodeInspectorWidgets.js";

const DEFAULT_ITEM_KEY = "_loop_item";
const COLUMN_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Loop End inspector.
 *
 * The pair reference is shown READ-ONLY, by the start step's label. It is
 * stored as a node id, and a node id is the one thing that must never appear
 * on screen — so the control that would let you type one does not exist. The
 * pairing is made by adding the pair together, or by picking from the loop
 * starts actually present.
 */
export function OperationInspectorLoopEnd({
  selectedNode,
  setNodeData,
  nodes,
  edges,
  nodeOutputRecord,
  onRunNode,
  runBusy,
  operationStatus,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  nodes: Node<OperationCanvasNodeData>[];
  edges: Edge[];
  nodeOutputRecord?: NodeOutputRecord;
  onRunNode: (nodeId: string) => void | Promise<void>;
  runBusy: boolean;
  operationStatus: string;
}) {
  const config = selectedNode.data.config ?? {};
  const startId = typeof config.loop_start_node_id === "string" ? config.loop_start_node_id : "";
  const aggregation = config.aggregation === "none" ? "none" : "concat";
  const itemKeyRaw = typeof config.item_key_column === "string" ? config.item_key_column : DEFAULT_ITEM_KEY;
  const itemKeyOn = itemKeyRaw.trim() !== "";

  const update = (patch: Record<string, unknown>) =>
    setNodeData(selectedNode.id, (data) => ({ ...data, config: { ...data.config, ...patch } }));

  const loopStarts = useMemo(() => nodes.filter((n) => n.data.nodeType === "loop_start"), [nodes]);
  const startNode = loopStarts.find((n) => n.id === startId) ?? null;
  const startLabel = startNode ? startNode.data.label?.trim() || NODE_LABELS.loop_start : null;

  const graph = useMemo(
    () => ({
      nodes: nodes.map((n) => ({ id: n.id, type: n.data.nodeType, config: n.data.config })),
      edges: edges.map((e) => ({ edge_type: "data", from_node: e.source, to_node: e.target })),
    }),
    [nodes, edges]
  );
  const pairing = useMemo(() => resolveLoopPairing(graph), [graph]);
  const claimedByAnother = pairing.duplicateEnds.some((d) => d.endIds.includes(selectedNode.id));

  const resultEdge = edges.find((e) => e.target === selectedNode.id);
  const keyValid = !itemKeyOn || COLUMN_NAME_RE.test(itemKeyRaw.trim());

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{loopPairName(startLabel, "end")}</div>

        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ fontSize: 13 }}>
            Closes:{" "}
            {startLabel ? (
              <strong>{startLabel}</strong>
            ) : (
              <span className="muted">not paired yet</span>
            )}
          </div>

          {/* Choosing WHICH loop is a pick from the loop starts that exist —
              never a typed id. When there is exactly one, the pairing is
              already unambiguous and a picker would be noise. */}
          {loopStarts.length > 1 || !startLabel ? (
            <label className="field">
              <span className="label">Which loop does this close?</span>
              <select
                className="select"
                value={startId}
                onChange={(e) => update({ loop_start_node_id: e.target.value })}
              >
                <option value="">Choose a loop start…</option>
                {loopStarts.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.data.label?.trim() || NODE_LABELS.loop_start}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {claimedByAnother ? (
            <div className="help">
              Another loop end already closes that loop. A loop finishes in exactly one place — point this at a
              different loop, or remove it.
            </div>
          ) : null}

          <label className="field">
            <span className="label">What this step emits</span>
            <select className="select" value={aggregation} onChange={(e) => update({ aggregation: e.target.value })}>
              <option value="concat">Every item's rows, stacked into one table</option>
              <option value="none">Nothing — the steps inside write their own output</option>
            </select>
          </label>
          <div className="help">
            {aggregation === "concat"
              ? resultEdge
                ? "Each item's result is stacked in the order the items ran."
                : "Nothing is connected to this step yet — wire the last step of the loop into it."
              : "Use this when a step inside the loop writes to a stored dataset. The run log is still produced either way."}
          </div>

          <label className="field">
            <span className="label">
              <input
                type="checkbox"
                checked={itemKeyOn}
                onChange={(e) => update({ item_key_column: e.target.checked ? DEFAULT_ITEM_KEY : "" })}
                style={{ marginRight: 6 }}
              />
              Stamp which item each row came from
            </span>
          </label>
          {itemKeyOn ? (
            <>
              <label className="field">
                <span className="label">Column name</span>
                <input
                  className="input"
                  value={itemKeyRaw}
                  onChange={(e) => update({ item_key_column: e.target.value })}
                />
              </label>
              <div className="help">
                {keyValid
                  ? "The run log carries the same value, so the two outputs can be joined."
                  : "Start with a letter or underscore; letters, numbers and underscores only."}
              </div>
            </>
          ) : (
            <div className="help">
              Without it the combined table cannot say which item produced which row. Turn it off only when a strict
              output shape cannot carry an extra column.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <strong>Run the whole loop</strong>
          <button
            className="btn btnSm"
            type="button"
            disabled={runBusy || operationStatus === "validate" || !startLabel}
            onClick={() => void onRunNode(selectedNode.id)}
            title={
              operationStatus === "validate"
                ? "Operation requires validation"
                : !startLabel
                  ? "Pair this step with a loop start first"
                  : "Process every item and produce the combined result and the run log"
            }
          >
            {runBusy ? "Running…" : "Run every item"}
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 12, marginBottom: 12 }}>
          Running this step runs the WHOLE loop — every item — and produces both outputs.
        </p>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Run log</div>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          This step has a second output carrying one row per item — which item, whether it succeeded, failed, or was
          never attempted, and the error when it failed. Wire it into a table to read it, or into a step that acts on
          the failures.
        </p>
      </div>

      {nodeOutputRecord ? <NodeOutputStats rec={nodeOutputRecord} /> : null}
    </div>
  );
}
